import type { Direction } from '@sentra/contracts';
import { LogIn, LogOut } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';

import { Button } from '@/design/components/button';
import { cn } from '@/design/utils';

/**
 * The one control that records a movement, and the one place that decides how a direction looks.
 *
 * **It sits above the feature folders because two features render it**, exactly as
 * `features/typed-confirm.tsx` does: the gate's resolve panel (`features/scan/resolve.tsx`) and the
 * record screen's manual movement (`features/persons/record/manual-movement.tsx`). They used to draw
 * the pair separately and had already drifted — the gate carried icons and a `data-direction`
 * attribute and the record carried neither, so the same act looked like two different acts depending
 * on which screen an operator reached it from.
 *
 * **An entry is green and an exit is red, and that is an amendment of record.** `design/tokens.css`
 * reserves those two colours for "accepted" and "refused" and says they mean nothing else; they now
 * also carry a movement's DIRECTION. What makes that safe is that the two buttons are a PAIR, each
 * carrying its own word and its own icon, so nothing here is told by colour alone; that the gate's
 * outcome banners are untouched; and that the full-screen anomaly alert is still the only red that
 * fills a screen. Where the two reds do meet — a failed movement on the record screen, beneath a red
 * exit button that is the common case in a residence — the refusal is drawn as a bordered BANNER
 * rather than as loose red text, so the two are different objects rather than two red things stacked.
 *
 * **The emphasis is still a solid fill against an outline, not colour.** The direction the record expects is the
 * solid, wider one; the other is the same hue as an outline. An operator who cannot tell the two hues
 * apart still reads which one the system is expecting, which is the property the panel had before
 * this file existed and had to keep.
 *
 * **Measured, not chosen.** `bg-success` on `text-success-foreground` is 5.05 light and 7.00 dark;
 * `bg-danger` on `text-danger-foreground` is 5.71 and 5.22 — against a floor of 4.5 for text this
 * size. The outline forms are 5.20/6.48 and 5.87/4.84 on a card. `shell.web.test.tsx` recomputes all
 * of them from `tokens.css` on every run, so a brighter token fails there rather than in an audit.
 *
 * **Neither hover is translucent any more, and that was not cosmetic.** Both used to tint whatever
 * was behind them, which gives a control one contrast ratio per backdrop rather than one ratio: the
 * entry outline hovered on the RECORD screen, where the page is behind it rather than a card,
 * measured 4.42:1 against a 4.5 floor, and the exit outline in the dark theme landed on 4.50 exactly.
 * The same hover on the gate's resolve panel passed at 4.54 — the only difference being which screen
 * the operator happened to be on. They now hover to the opaque semantic surfaces, 4.73 and 5.28 in
 * the light theme and 6.36 and 4.82 in the dark, and the solid pair hovers to the opaque `-hover`
 * fills, which move away from the page rather than towards it.
 */

/** The icon each direction carries, so no call site chooses one. */
const DIRECTION_ICON = {
  entry: LogIn,
  exit: LogOut,
} as const satisfies Record<Direction, typeof LogIn>;

/**
 * The expected direction: a solid fill of its own colour.
 *
 * Three utilities and no more, because `cn` is `twMerge(clsx(...))` and the caller's class wins every
 * conflict: these take the background, the ink and the hover of the `default` variant and nothing
 * else. The names are deliberately not quoted here — Tailwind scans this file as plain text, so a
 * class named in a comment is a class it generates, and a retired one named in prose is a rule that
 * never leaves the stylesheet.
 */
const DIRECTION_FILL = {
  entry: 'bg-success text-success-foreground hover:bg-success-hover',
  exit: 'bg-danger text-danger-foreground hover:bg-danger-hover',
} as const satisfies Record<Direction, string>;

/**
 * The other direction: the same hue drawn as an outline, so the pair reads as one control.
 *
 * **The one `dark:` entry left is load-bearing and is not duplication.** tailwind-merge treats a
 * modifier as part of the conflict key, so a bare `border-success` does NOT take the `outline`
 * variant's dark border with it, and without `dark:border-success` the colour is lost in the dark
 * theme ONLY — the shape of defect nobody notices, because the screen an operator uses is usually
 * the other one.
 *
 * **`dark:bg-background` stays, and the reason moved rather than went away.** It used to displace a
 * per-theme background the base `outline` variant no longer has; what it does now is keep this one
 * button OFF the raised control surface in the dark theme, because the tone it draws its label in
 * needs the page behind it: `text-danger` is 5.22:1 on the page, 4.84 on a card and 4.43 on
 * `--control`, and only the first two clear the floor. The accessibility sweep caught this the first
 * time the class was dropped, on the accepted-outcome view that sweep had never looked at before.
 *
 * The per-theme HOVER entries are gone, though, because one opaque semantic surface now serves both
 * themes where two tints used to.
 */
const DIRECTION_OUTLINE = {
  entry:
    'border-success text-success hover:bg-success-surface hover:text-success dark:border-success dark:bg-background',
  exit: 'border-danger text-danger hover:bg-danger-surface hover:text-danger dark:border-danger dark:bg-background',
} as const satisfies Record<Direction, string>;

export function DirectionButton({
  direction,
  expected,
  label,
  // **Defaulted rather than left to the call site.** The design-system `Button` sets no default type,
  // so an untyped one inside a `<form>` is a SUBMIT. Neither call site is in a form today; the first
  // one that is would otherwise submit it by recording a movement.
  type = 'button',
  ...rest
}: Readonly<{
  direction: Direction;
  /** Whether this is the direction the record expects. Drawn solid, and wider. */
  expected: boolean;
  /**
   * The already-translated label.
   *
   * A string rather than a catalogue key, because the two screens name this act differently and one
   * of them swaps in "Recording…" mid-submission — and because `test:i18n` parses `t()` at the call
   * site, which is where it belongs.
   */
  label: string;
}> &
  Omit<ComponentProps<typeof Button>, 'variant' | 'className' | 'children'>): ReactNode {
  const Icon = DIRECTION_ICON[direction];

  return (
    <Button
      type={type}
      // The direction as a closed-set token: the label is prose in two languages and the verb is not,
      // and every end-to-end flow that records a movement finds the button by this.
      data-direction={direction}
      variant={expected ? 'default' : 'outline'}
      className={cn(
        expected ? DIRECTION_FILL[direction] : DIRECTION_OUTLINE[direction],
        expected && 'min-w-48',
      )}
      {...rest}
    >
      <Icon aria-hidden />
      {label}
    </Button>
  );
}
